import { useEffect, useState } from "react";
import { api, ROLE_LABELS, ROLE_COLORS, can } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Trash2, Search, Siren } from "lucide-react";

const ROLES = ["founder","admin","officer","member","candidate"];

export default function Members() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const canManageRoles = can(user.role, "founder", "admin");
  const canDelete = can(user.role, "founder");

  const [isOfficerManager, setIsOfficerManager] = useState(false);
  const [addOfficerOpen, setAddOfficerOpen] = useState(false);

  const load = () => api.get("/users").then(r => setUsers(r.data));
  useEffect(() => {
    load();
    api.get("/memur/me").then(r => setIsOfficerManager(!!r.data.is_officer_manager)).catch(() => setIsOfficerManager(false));
  }, []);

  const changeRole = async (u, role) => {
    try { await api.put(`/users/${u.id}/role`, { role }); toast.success("Rol güncellendi"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Başarısız"); }
  };

  const remove = async (u) => {
    if (!window.confirm(`${u.discord_username} silinsin mi?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Silindi"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Başarısız"); }
  };

  const filtered = users.filter(u => {
    const s = q.toLowerCase();
    return !s || u.discord_username.toLowerCase().includes(s) || u.hex_id.toLowerCase().includes(s) || (u.display_name||"").toLowerCase().includes(s);
  });

  return (
    <div className="p-6 md:p-10 space-y-6 max-w-6xl">
      <header className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] font-mono text-rose-500 tracking-widest mb-2">// ROSTER</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight">Üyeler</h1>
          <p className="text-zinc-500 mt-2">Deulsra üye kadrosu ve rol yönetimi.</p>
        </div>
        {isOfficerManager && (
          <Button onClick={() => setAddOfficerOpen(true)} data-testid="add-officer-button"
            className="bg-sky-600 hover:bg-sky-700"><Siren className="w-4 h-4 mr-2" />Memur Ekle</Button>
        )}
      </header>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <Input value={q} onChange={e=>setQ(e.target.value)} placeholder="Ara: kullanıcı adı, hex, gösterim adı..." data-testid="members-search-input"
          className="bg-zinc-950 border-zinc-800 pl-10 focus-visible:ring-rose-500" />
      </div>

      <Card className="bg-zinc-950 border-zinc-900 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-900/50">
              <tr className="text-left text-[10px] font-mono text-zinc-500 tracking-widest">
                <th className="px-4 py-3">ÜYE</th>
                <th className="px-4 py-3">HEX ID</th>
                <th className="px-4 py-3">DISCORD ID</th>
                <th className="px-4 py-3">ROL</th>
                <th className="px-4 py-3">KAYIT</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(u => (
                <tr key={u.id} className="border-t border-zinc-900 hover:bg-zinc-900/40" data-testid={`member-row-${u.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {u.discord_avatar ? <img src={u.discord_avatar} alt="" className="w-8 h-8 rounded-full" /> :
                        <div className="w-8 h-8 rounded-full bg-zinc-800" />}
                      <div>
                        <div className="font-medium">{u.display_name || u.discord_username}</div>
                        <div className="text-xs text-zinc-500 font-mono">@{u.discord_username}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-400">{u.hex_id}</td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-500">{u.discord_id}</td>
                  <td className="px-4 py-3">
                    {canManageRoles && u.id !== user.id ? (
                      <Select value={u.role} onValueChange={(v)=>changeRole(u, v)}>
                        <SelectTrigger data-testid={`role-select-${u.id}`} className={`h-8 w-36 bg-zinc-900 border ${ROLE_COLORS[u.role]}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-950 border-zinc-800">
                          {ROLES.map(r => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className={`text-[10px] font-mono px-2 py-1 border ${ROLE_COLORS[u.role]}`}>{ROLE_LABELS[u.role]}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-zinc-500 font-mono">{new Date(u.created_at).toLocaleDateString("tr-TR")}</td>
                  <td className="px-4 py-3">
                    {canDelete && u.id !== user.id && u.role !== "founder" && (
                      <Button size="sm" variant="ghost" onClick={()=>remove(u)} data-testid={`delete-user-${u.id}`} className="text-rose-400 hover:bg-rose-950/30"><Trash2 className="w-4 h-4" /></Button>
                    )}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-zinc-500">Sonuç yok.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <AddOfficerDialog open={addOfficerOpen} onOpenChange={setAddOfficerOpen} />
    </div>
  );
}

function AddOfficerDialog({ open, onOpenChange }) {
  const [eligible, setEligible] = useState([]);
  const [ranks, setRanks] = useState([]);
  const [userId, setUserId] = useState("");
  const [icIsim, setIcIsim] = useState("");
  const [rankId, setRankId] = useState("");

  useEffect(() => {
    if (!open) return;
    api.get("/memur/eligible-users").then(r => setEligible(r.data)).catch(() => setEligible([]));
    api.get("/memur/ranks").then(r => setRanks(r.data)).catch(() => setRanks([]));
    setUserId(""); setIcIsim(""); setRankId("");
  }, [open]);

  const submit = async () => {
    if (!userId || !icIsim.trim() || !rankId) {
      toast.error("Tüm alanları doldurun");
      return;
    }
    try {
      await api.post("/memur/officers", { user_id: userId, ic_isim: icIsim, rank_id: rankId });
      toast.success("Memur eklendi");
      onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Başarısız"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-950 border-zinc-800">
        <DialogHeader><DialogTitle>Memur Ekle</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-zinc-500">Üye</label>
            <Select value={userId} onValueChange={setUserId}>
              <SelectTrigger data-testid="add-officer-user-select" className="bg-zinc-900 border-zinc-800"><SelectValue placeholder="Üye seçin" /></SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800 max-h-64">
                {eligible.map(u => <SelectItem key={u.id} value={u.id}>@{u.discord_username} — {u.hex_id}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-zinc-500">IC İsim</label>
            <Input value={icIsim} onChange={e => setIcIsim(e.target.value)} placeholder="Örn. John Doe"
              data-testid="add-officer-icisim-input" className="bg-zinc-900 border-zinc-800" />
          </div>
          <div>
            <label className="text-xs text-zinc-500">Rütbe</label>
            <Select value={rankId} onValueChange={setRankId}>
              <SelectTrigger data-testid="add-officer-rank-select" className="bg-zinc-900 border-zinc-800"><SelectValue placeholder="Rütbe seçin" /></SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800">
                {ranks.sort((a,b)=>a.level-b.level).map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} data-testid="add-officer-submit" className="bg-sky-600 hover:bg-sky-700">Memur Olarak Ekle</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
